import { useCallback, useEffect, useMemo, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded'
import SystemUpdateRoundedIcon from '@mui/icons-material/SystemUpdateRounded'
import { Button, EmptyHubState, RailChipTabs, SettingsReadOnlyRow, SettingsRow, SettingsSection } from '../Common'
import {
  applyNativeUpdateNow,
  checkNativeUpdateNow,
  copyLatestApkDownloadLink,
  fetchPublishedManifestResult,
  getLatestApkDownloadUrl,
  getPendingNativeUpdate,
  openLatestApkDownload,
  shareLatestApkDownloadLink,
  type PublishedManifest,
  type UpdateSurface,
} from '../../services/nativeUpdateService'
import { getWrapperRole } from '../../utils/wrapperRole'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'

const formatReleaseDate = (iso: string | null): string => {
  if (!iso) return ''
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  try {
    return parsed.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return parsed.toISOString()
  }
}

const formatBytes = (bytes: number | null): string => {
  if (bytes === null || bytes <= 0) return ''
  const mb = bytes / (1024 * 1024)
  if (mb >= 10) return `${Math.round(mb)} MB`
  return `${mb.toFixed(1)} MB`
}

type DevicesPanelProps = {
  /** Keeps the desktop surface unchanged while hiding implementation URLs in Member chrome. */
  memberMobile?: boolean
}

export const DevicesPanel = ({ memberMobile = false }: DevicesPanelProps) => {
  void memberMobile
  const isNative = Capacitor.isNativePlatform()
  const wrapperRole = getWrapperRole()
  const { showToast } = useToast()
  const { t } = useNls()
  const [surface, setSurface] = useState<UpdateSurface>(
    wrapperRole === 'helper' ? 'helper' : 'member',
  )
  const [manifest, setManifest] = useState<PublishedManifest | null>(null)
  const [manifestError, setManifestError] = useState(false)
  const [busy, setBusy] = useState<
    'download' | 'copy' | 'share' | 'check' | 'install' | null
  >(null)

  const _downloadUrl = useMemo(
    () => getLatestApkDownloadUrl(surface === 'helper' ? 'helper' : 'member'),
    [surface],
  )
  void _downloadUrl

  const [prevSurface, setPrevSurface] = useState(surface)
  if (surface !== prevSurface) {
    setPrevSurface(surface)
    setManifest(null)
    setManifestError(false)
  }

  const loadManifest = useCallback(() => {
    let cancelled = false
    void fetchPublishedManifestResult(surface).then((result) => {
      if (cancelled) return
      if (!result.ok) {
        setManifest(null)
        setManifestError(true)
        return
      }
      setManifest(result.data)
    })
    return () => {
      cancelled = true
    }
  }, [surface])

  useEffect(() => loadManifest(), [loadManifest])

  const downloadApk = useCallback(() => {
    setBusy('download')
    showToast('Opening the APK download in a new tab…', 'info')
    openLatestApkDownload()
    setBusy(null)
  }, [showToast])

  const copyLink = useCallback(async () => {
    setBusy('copy')
    try {
      await copyLatestApkDownloadLink()
      showToast(t('NLSSettingsToastLinkCopied'), 'success')
    } catch {
      showToast('Copy is unavailable in this browser.', 'error')
    } finally {
      setBusy(null)
    }
  }, [showToast, t])

  const shareLink = useCallback(async () => {
    setBusy('share')
    try {
      const result = await shareLatestApkDownloadLink()
      showToast(
        result === 'SHARED'
          ? 'Android app download link shared.'
          : t('NLSSettingsToastLinkCopied'),
        'success',
      )
    } catch {
      showToast(t('NLSSettingsToastShareFailed'), 'error')
    } finally {
      setBusy(null)
    }
  }, [showToast, t])

  const checkUpdate = useCallback(async () => {
    setBusy('check')
    showToast('Checking for the latest bundle…', 'info')
    const result = await checkNativeUpdateNow()
    showToast(result.message, result.status !== 'ERROR' ? 'success' : 'error')
    setBusy(null)
  }, [showToast])

  const installUpdate = useCallback(async () => {
    setBusy('install')
    showToast('Installing update…', 'info')
    const result = await applyNativeUpdateNow(getPendingNativeUpdate())
    showToast(result.message, result.status !== 'ERROR' ? 'success' : 'error')
    setBusy(null)
  }, [showToast])

  const versionText = manifest
    ? [
        manifest.version,
        manifest.releasedAt ? `Released ${formatReleaseDate(manifest.releasedAt)}` : null,
        formatBytes(manifest.apkSizeBytes) ? `APK ${formatBytes(manifest.apkSizeBytes)}` : null,
      ].filter(Boolean).join(' · ')
    : 'No build details on store bucket.'

  return (
    <div className="skSectionStack">
      {!isNative ? (
        <SettingsSection
          title={t('NLSSettingsSectionPhoneApps')}
          description="Install the Android app for Member or Helper. Sign in with the same account and your workspaces sync automatically."
        >
          <div className="skSectionIntro">
            <p>Get Timelining on your phone</p>
          </div>
          <SettingsRow
            label="App surface"
            hint="Choose between Member and Helper app builds for Android."
            control={
              <RailChipTabs<UpdateSurface>
                orientation="horizontal"
                ariaLabel="App surface"
                value={surface}
                onChange={(val) => setSurface(val)}
                items={[
                  { key: 'member', label: 'Member app' },
                  { key: 'helper', label: 'Helper app' },
                ]}
              />
            }
          />
          {manifestError ? (
            <div className="skSectionErrorBlock">
              <EmptyHubState
                preset="devices"
                variant="error"
                compact
                title="Update manifest is unavailable"
                description="Published build details could not be loaded for this device surface."
              />
              <Button variant="secondary" size="sm" onClick={() => loadManifest()}>
                Retry
              </Button>
            </div>
          ) : (
            <SettingsReadOnlyRow
              label={t('NLSSettingsLabelLatestVersion')}
              value={versionText}
            />
          )}
          <SettingsRow
            label="Install package"
            hint="Download the APK directly, copy link, or share with your mobile device."
            control={
              <div className="skButtonGroup">
                <Button
                  variant="primary"
                  size="sm"
                  icon={<DownloadRoundedIcon />}
                  onClick={downloadApk}
                  disabled={busy !== null}
                >
                  Download APK
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<ContentCopyRoundedIcon />}
                  onClick={() => void copyLink()}
                  disabled={busy !== null}
                >
                  {t('NLSSettingsActionCopyLink')}
                </Button>
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={<IosShareRoundedIcon />}
                  onClick={() => void shareLink()}
                  disabled={busy !== null}
                >
                  {t('NLSSettingsActionShare')}
                </Button>
              </div>
            }
          />
        </SettingsSection>
      ) : null}

      {isNative ? (
        <SettingsSection
          title="App updates"
          description="Updates are downloaded automatically in the background. Use Install now to switch to the latest bundle immediately."
        >
          <SettingsRow
            label="Bundle update"
            hint="Check for the newest native bundle and install it immediately."
            control={
              <div className="skButtonGroup">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<SystemUpdateRoundedIcon />}
                  onClick={() => void checkUpdate()}
                  disabled={busy !== null}
                >
                  {t('NLSSettingsActionCheckUpdates')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<DownloadRoundedIcon />}
                  onClick={() => void installUpdate()}
                  disabled={busy !== null}
                >
                  Install now
                </Button>
              </div>
            }
          />
        </SettingsSection>
      ) : null}
    </div>
  )
}
